import { getJwtToken } from "@/lib/auth-client";

/**
 * Runtime-safe base64url decode — works in browsers (atob) and Node
 * (Buffer fallback) so this module can be imported from tests/SSR.
 */
function base64UrlDecode(input: string): string {
  const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    "=",
  );
  const g = globalThis as { atob?: (s: string) => string };
  if (typeof g.atob === "function") return g.atob(padded);
  return Buffer.from(padded, "base64").toString("binary");
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    return JSON.parse(base64UrlDecode(payload)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Reads `siteClaims` from the in-memory JWT. Returns `[]` when no token
 * is present or the claim is missing/malformed — callers that need to
 * distinguish "no token yet" from "token says zero claims" should use
 * `hasLoadedZeroSiteClaims` instead.
 */
export function readSiteClaims(): string[] {
  const token = getJwtToken();
  if (!token) return [];
  const payload = decodeJwtPayload(token);
  const claims = payload?.siteClaims;
  return Array.isArray(claims)
    ? claims.filter((c): c is string => typeof c === "string")
    : [];
}

/**
 * True only when a JWT has been loaded *and* its `siteClaims` array is
 * empty. Returns false while the token is still being fetched, so a
 * not-yet-loaded token can't falsely lock out a valid user.
 */
export function hasLoadedZeroSiteClaims(): boolean {
  const token = getJwtToken();
  if (!token) return false;
  return readSiteClaims().length === 0;
}
